import { Dialog, type DialogProps } from "@verde/compose-ui";
import {
  type FetchClient,
  creditUsageQueryKeys,
  useFetchCreditsCostPerConsult,
  useFetchWalletBalance,
  useSubtractWalletCredit,
  walletQueryKeys,
} from "@verde/entities";
import { CircleStackIcon, ExclamationTriangleIcon } from "@verde/icons";
import { Alert, Button, Skeleton } from "@verde/ui-core";
import { formatDate, queryClient } from "@verde/utils";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface CreditUsageDialogProps extends Pick<DialogProps, "onClose"> {
  client?: FetchClient;
  onBuy?: () => void;
  refresh?: boolean;
  creditUsageId?: number;
}

function CreditUsageDialog({
  client,
  creditUsageId,
  onBuy,
  onClose,
}: CreditUsageDialogProps) {
  const costs = useFetchCreditsCostPerConsult();
  const subtract = useSubtractWalletCredit();
  const balance = useFetchWalletBalance();
  const params = useParams();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCost = useMemo(() => costs.data?.[0], [costs.data]);
  const isLoading = useMemo(
    () => costs.isLoading || balance.isLoading,
    [costs.isLoading, balance.isLoading],
  );
  const hasBalanceToBuy = useMemo(() => {
    if (!selectedCost || !balance.data) return false;

    return selectedCost.value <= balance.data?.value;
  }, [selectedCost, balance.data]);

  const handleUse = useCallback(async () => {
    if (hasBalanceToBuy === false) {
      onBuy?.();
      return;
    }

    if (!client || !selectedCost) return;

    try {
      setIsSubmitting(true);

      await subtract.mutateAsync({
        client_id: client.id,
        kinds: ["all"],
        value: selectedCost.value,
      });

      if (params?.credit_usage_id) {
        navigate(`/raio-x-rural/${client.id}`);
      }

      await queryClient.invalidateQueries(walletQueryKeys.balance());
      await queryClient.invalidateQueries(
        creditUsageQueryKeys.by_client(client.id),
      );
      await queryClient.invalidateQueries(
        creditUsageQueryKeys.detail.root(creditUsageId),
      );

      onClose?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    params,
    navigate,
    client,
    subtract,
    creditUsageId,
    selectedCost,
    onBuy,
    hasBalanceToBuy,
    onClose,
  ]);

  const lastReportDate = useMemo(() => {
    return formatDate(client?.rating?.created_at);
  }, [client?.rating?.created_at]);

  return (
    <Dialog
      open
      size="sm"
      onClose={onClose}
      header={{
        size: "lg",
        title: "Usar créditos para consultar",
        icon: <CircleStackIcon />,
        kind: "error",
      }}
    >
      <div className="grid gap-4">
        {isLoading && <Skeleton height={150} />}

        {!isLoading && (
          <>
            <p className="text-lg font-bold text-neutral-dark-2">
              {hasBalanceToBuy ? "Tem certeza?" : "Saldo insuficiente"}
            </p>
            {lastReportDate && (
              <Alert kind="error" icon={<ExclamationTriangleIcon />}>
                {/*TODO: Quando tiver históricos de consulta de rating pelo AA liberar esta forma*/}
                {/* Esse produtor já foi consultado recentemente: {lastReportDate}. */}
                Última consulta deste produtor: {lastReportDate}.
              </Alert>
            )}

            <Alert>
              {hasBalanceToBuy
                ? `Ao realizar essa ação, será usado ${selectedCost?.value} de seus
              créditos.`
                : "Seu saldo é insuficiente para esta consulta."}
            </Alert>

            <hr />

            <Button
              icon={<CircleStackIcon />}
              size="lg"
              loading={isSubmitting}
              onClick={handleUse}
            >
              {hasBalanceToBuy ? "Usar créditos" : "Comprar créditos"}
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}

export default React.memo(CreditUsageDialog);
